import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import type { RefresherEventDetail } from '@ionic/core/components';
import {
  IonContent,
  IonCard,
  IonPage,
  IonItem,
  IonGrid,
  IonRow,
  IonCol,
  IonRefresher,
  IonRefresherContent,
  useIonRouter,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonIcon,
  useIonToast,
  IonSelect,
  IonSelectOption,
} from '@ionic/react';
import { analytics, chevronDownCircleOutline, filterOutline } from 'ionicons/icons';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Map, { FullscreenControl, GeolocateControl, Marker, NavigationControl } from 'react-map-gl';
import Plot from 'react-plotly.js';

import AssetIconBulb from '../assets/icons/mapAssetIcon.png';
import Satellite from '../assets/icons/satellite.png';
import Street from '../assets/icons/street.png';
import urgent from '../assets/tabler_urgent.svg';
import axios from '../axios';
import AssetOnMapModal from '../components/AssetOnMapModal';
import Header from '../components/Header';
import ScanButton from '../components/ScanButton';
import { AppContext } from '../providers/AppContext';
import { constant, getDashboard, } from '../utils';
import LocationService from '../utils/locationService';


import 'mapbox-gl/dist/mapbox-gl.css';
import 'leaflet/dist/leaflet.css';



const Home: React.FC = () => {

  const { t } = useTranslation();
  const router = useIonRouter();
  const [present] = useIonToast();

  let mapRef: any = useRef();

  const [watchId, setWatchId] = useState<any>();

  const { state, dispatch } = useContext(AppContext);

  // const [gpsHome, setGpsHome] = useState({
  //   lat: 0,
  //   lon: 0
  // })
  const [style, setStyle] = useState(localStorage.getItem('style') || 'mapbox://styles/mapbox/satellite-v9');
  const [styleToggleVar, setStyleToggleVar] = useState(localStorage.getItem('style') === 'mapbox://styles/mapbox/satellite-v9' || localStorage.getItem('style') === null ? true : false);

  const [filter, setFilter] = useState('');
  const [filterList, setFilterList] = useState([]);
  const goTo = (route: string) => {
    router.push(route);
  };

  const [full, setFull] = useState(false);

  const [assetId, setAssetId] = useState('')
  const [assetLayoutModal, setAssetLayoutModal] = useState(false)


  const getDashboardData = async (status: any) => {
    const res: any = await getDashboard(status, '');
    dispatch({ type: 'dashboard', payload: res || {} });
    dispatch({ type: 'map', payload: res?.geo_map_data || [] });
  }

  useEffect(() => {
    getDashboardData('');
    checkPermissions();
    getProfilePhoto();
    getFilterDropdown();
    if (localStorage.getItem('style') === 'mapbox://styles/mapbox/satellite-v9') {
      setStyleToggleVar(true);
    }

    ["fullscreenchange", "webkitfullscreenchange", "mozfullscreenchange", "msfullscreenchange"].forEach(
      eventType => document.addEventListener(eventType, checkExpand, true)
    );
  }, []);

  const checkExpand = () => {
    setAssetLayoutModal(false);
    if (document.fullscreenElement) {
      setFull(true);
    }
    else {
      setFull(false);
    }
  }

  // Handle the back button press
  useEffect(() => {
    const handleBackButton = (event: any) => {
      if (event?.target?.fullscreen) {
        document.exitFullscreen();
      }
    };
    document.addEventListener('ionBackButton', handleBackButton);
    return () => {
      document.removeEventListener('ionBackButton', handleBackButton);
    };
  }, []);

  function doRefresh(event: CustomEvent<RefresherEventDetail>) {
    getDashboardData('');
    setTimeout(() => {
      event.detail.complete();
    }, 2000);
  }

  const getFilterDropdown = async () => {
    const url = '/method/preflet_asset.api.get_control_location_dropdown';
    const config: any = {
      method: 'get',
      url,
      headers: {
        Authorization: `Basic ${localStorage.getItem('hyprism_login')}`,
      },
    };
    axios(config)
      .then((response: any) => {
        setFilterList(response?.data?.message);
      })
      .catch((e: any) => {
        console.log('error', e);
      });
  };


  /* Check if Object in empty */

  const isEmpty = (obj: any) => {
    // null and undefined are "empty"
    if (obj == null) return true;

    // Assume if it has a length property with a non-zero value
    // that that property is correct.
    if (obj.length > 0) return false;
    if (obj.length === 0) return true;

    // If it isn't an object at this point
    // it is empty, but it can't be anything *but* empty
    // Is it empty?  Depends on your application.
    if (typeof obj !== 'object') return true;

    // Otherwise, does it have any properties of its own?
    // Note that this doesn't handle
    // toString and valueOf enumeration bugs in IE < 9
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) return false;
    }

    return true;
  };

  const getProfilePhoto = () => {
    const url = '/method/preflet_asset.api.get_profile_details';

    const config: any = {
      method: 'get',
      url,
      headers: {
        Authorization: `Basic ${localStorage.getItem('hyprism_login')}`,
      },
    };

    try {
      axios(config).then((response) => {
        if (response.data.data.user_image)
          localStorage.setItem(constant.PROFILE_PHOTO, response.data.data.user_image);
      });
    } catch (error: any) {
      console.log(error);
    }
  }

  const openAssets = (id: any) => {
    if (id) {
      setAssetId(id);
      setAssetLayoutModal(!assetLayoutModal);
      setAssetLayoutModal(true)
    }
  }

  const colors = ['#7AABE5', '#FF9088', '#FCB454', '#61DBA7'];

  const toggleStyle = (val: boolean) => {
    setStyleToggleVar(val);
    setStyle(val ? 'mapbox://styles/mapbox/satellite-v9' : 'mapbox://styles/mapbox/streets-v12');
    localStorage.setItem('style', val ? 'mapbox://styles/mapbox/satellite-v9' : 'mapbox://styles/mapbox/streets-v12');
  }

  const checkPermissions = async () => {
    const hasPermission = await LocationService.checkGPSPermission();
    if (hasPermission) {
      if (Capacitor.isNativePlatform()) {
        const canUseGPS = await LocationService.askToTurnOnGPS();
        postGPSPermission(canUseGPS);
      }
      else {
        postGPSPermission(true);
      }
    }
    else {
      const permission = await LocationService.requestGPSPermission();
      if (permission === 'CAN_REQUEST' || permission === 'GOT_PERMISSION') {
        if (Capacitor.isNativePlatform()) {
          const canUseGPS = await LocationService.askToTurnOnGPS();
          if (canUseGPS)
            postGPSPermission(canUseGPS);
          else
            present({
              message: t('Utils.gpsDenied'),
              color: 'danger',
              duration: 4000,
            });
        }
        else {
          postGPSPermission(true);
        }
      }
      else {
        present({
          message: t('Utils.gpsDenied'),
          color: 'danger',
          duration: 4000,
        });
      }
    }
  }

  const postGPSPermission = async (canUseGPS: boolean) => {
    if (canUseGPS) {
      watchPosition();
    }
  }

  const watchPosition = async () => {
    try {
      const w_id = await Geolocation.watchPosition({}, (position: any, err: any) => {
        if (err) {
          present({
            message: t('Utils.gpsError'),
            color: 'danger',
            duration: 4000,
          });
          return;
        }
        // const temp = {
        //   lat: position.coords.latitude.toFixed(3),
        //   lon: position.coords.longitude.toFixed(3)
        // }
        // setGpsHome(temp)
      })
      setWatchId(w_id);
      clearWatch();
    }
    catch (err) { console.log('err', err) }
  }

  const clearWatch = () => {
    try {
      if (watchId != null) {
        Geolocation.clearWatch({ id: watchId });
      }
    } catch (error: any) {
      console.log('error', error);
    }
  }

  const filterByCl = (status: any) => {
    setFilter(status || '');
    getDashboardData(status || '');
  }

  const FlyToLocation = () => {
    mapRef.flyTo({
      center: [state.map[0].lon, state.map[0].lat],
      zoom: 10,
      essential: true // this animation is considered essential with respect to prefers-reduced-motion
    });

  }

  return (
    <IonPage>
      <Header enableBackButton="" title="" />
      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={doRefresh}>
          <IonRefresherContent
            pullingIcon={chevronDownCircleOutline}
            pullingText={t('Utils.pullRefresh')}
            refreshingSpinner="circles"
            refreshingText={t('Utils.refreshing')}
          ></IonRefresherContent>
        </IonRefresher>

        {/* ── Hero Banner ── */}
        <div style={{
          background: 'linear-gradient(135deg, #1d508d 0%, #0f3460 100%)',
          padding: '16px 16px 28px',
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* decorative blobs */}
          <div style={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
          <div style={{ position: 'absolute', bottom: -20, left: -20, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />

          {/* ── Title + Filter in one row ── */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, position: 'relative' }}>

            {/* Left: title */}
            <div style={{ flexShrink: 0 }}>
              <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.6)', letterSpacing: '0.12em', textTransform: 'uppercase', lineHeight: 1 }}>
                {t('Home.Heading.Dashboard')}
              </p>
              {/* <h1 style={{ margin: '3px 0 0', fontSize: 22, fontWeight: 800, color: '#fff', lineHeight: 1.15 }}>Overview</h1> */}
            </div>

            {/* Right: filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, justifyContent: 'flex-end', minWidth: 0 }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                background: '#fff',
                borderRadius: 12,
                border: '1.5px solid #fff',
                paddingLeft: 8,
                paddingRight: 4,
                width: 'auto',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              }}>
                <IonIcon src={filterOutline} style={{ color: '#1d508d', fontSize: 14, flexShrink: 0 }} />
                <IonSelect
                  value={filter}
                  placeholder={t('Home.Heading.Filter')}
                  onIonChange={(e: any) => filterByCl(e.detail.value)}
                  style={{ '--color': '#1d1d1d', '--placeholder-color': '#888', '--placeholder-opacity': 1, fontSize: 13, width: 'auto' } as any}
                >
                  {filterList.map((item: any, index) => (
                    <IonSelectOption key={index} value={item.control_location}>{item.control_location}</IonSelectOption>
                  ))}
                </IonSelect>
              </div>
              {filter && (
                <button
                  onClick={() => filterByCl('')}
                  style={{
                    flexShrink: 0,
                    background: 'rgba(255,255,255,0.15)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    borderRadius: 10,
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 700,
                    padding: '7px 12px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('Home.Clear')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── Stat Cards ── */}
        <div style={{ padding: '0 12px', marginTop: -16, display: 'flex', gap: 10 }}>

          {/* Assets card */}
          <div
            onClick={() => goTo('assets')}
            style={{
              flex: 1,
              background: '#fff',
              borderRadius: 18,
              padding: '14px 12px',
              boxShadow: '0 4px 24px rgba(22,201,141,0.15)',
              border: '1.5px solid rgba(22,201,141,0.3)',
              cursor: 'pointer',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div style={{ position: 'absolute', top: -10, right: -10, width: 55, height: 55, borderRadius: '50%', background: 'rgba(22,201,141,0.1)' }} />
            <p style={{ margin: 0, fontSize: 10, fontWeight: 700, color: '#16c98d', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {t('Home.Card1.Cont')}
            </p>
            <h2 style={{ margin: '4px 0 0', fontSize: 30, fontWeight: 900, color: '#16c98d', lineHeight: 1 }}>
              {state.dashboard.need_installation_or_replacement_assets || '0'}
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: 9, color: '#999', fontWeight: 500 }}>tap to view</p>
          </div>

          {/* Documents card */}
          <div
            onClick={() => goTo('documents')}
            style={{
              flex: 1,
              background: '#fff',
              borderRadius: 18,
              padding: '14px 12px',
              boxShadow: '0 4px 24px rgba(253,118,137,0.15)',
              border: '1.5px solid rgba(253,118,137,0.3)',
              cursor: 'pointer',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div style={{ position: 'absolute', top: -10, right: -10, width: 55, height: 55, borderRadius: '50%', background: 'rgba(253,118,137,0.1)' }} />
            <p style={{ margin: 0, fontSize: 10, fontWeight: 700, color: '#fd7689', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {t('Home.Expiring')}
            </p>
            <h2 style={{ margin: '4px 0 0', fontSize: 30, fontWeight: 900, color: '#fd7689', lineHeight: 1 }}>
              {state.dashboard.cd_expiring_in_90_days || '0'}
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: 9, color: '#999' }}>{t('Home.within_90_days')}</p>
            <div style={{ marginTop: 8, paddingTop: 6, borderTop: '1px solid rgba(253,118,137,0.15)', display: 'flex', justifyContent: 'space-between' }}>
              <p style={{ margin: 0, fontSize: 9, color: '#bbb' }}>{t('Home.Total_Docs')}</p>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#555' }}>{state.dashboard.compliance_documents_count || '0'}</span>
            </div>
          </div>
        </div>

        {/* ── Overdue / Pending Row ── */}
        <div style={{ padding: '12px 12px 0' }}>
          <div style={{
            background: '#fff',
            borderRadius: 18,
            border: '1.5px solid rgba(15,84,169,0.2)',
            boxShadow: '0 4px 20px rgba(15,84,169,0.08)',
            overflow: 'hidden',
          }}>
            <div style={{ display: 'flex' }}>
              <div
                onClick={() => goTo('maintenanceHistory')}
                style={{ flex: 1, padding: '16px 16px', cursor: 'pointer' }}
              >
                <p style={{ margin: 0, fontSize: 10, fontWeight: 700, color: '#0f54a9', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  {t('Home.Overdue_Maintenance')}
                </p>
                <h2 style={{ margin: '4px 0 0', fontSize: 34, fontWeight: 900, color: '#0f54a9', lineHeight: 1 }}>
                  {state.dashboard.total_overdue_maintenance || '0'}
                </h2>
              </div>
              <div style={{ width: 1, background: 'rgba(15,84,169,0.1)', margin: '12px 0' }} />
              <div
                onClick={() => goTo('serviceHistory')}
                style={{ flex: 1, padding: '16px 16px', cursor: 'pointer' }}
              >
                <p style={{ margin: 0, fontSize: 10, fontWeight: 700, color: '#e67e22', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  {t('Home.Pending_Repairs')}
                </p>
                <h2 style={{ margin: '4px 0 0', fontSize: 34, fontWeight: 900, color: '#e67e22', lineHeight: 1 }}>
                  {state.dashboard.pending_repairs || '0'}
                </h2>
              </div>
            </div>
          </div>
        </div>

        {/* ── Map ── */}
        <div style={{ padding: '12px 12px 0' }}>
          <div style={{
            background: '#fff',
            borderRadius: 20,
            overflow: 'hidden',
            boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          }}>
            <div style={{
              padding: '12px 16px 10px',
              borderBottom: '1px solid rgba(0,0,0,0.05)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#1d508d' }}>{t('Home.map.map')}</p>
              {styleToggleVar
                ? <img src={Street} alt='street-icon' style={{ width: 32, height: 32, borderRadius: 8, cursor: 'pointer', objectFit: 'cover' }} onClick={() => toggleStyle(!styleToggleVar)} />
                : <img src={Satellite} alt='satellite-icon' style={{ width: 32, height: 32, borderRadius: 8, cursor: 'pointer', objectFit: 'cover' }} onClick={() => toggleStyle(!styleToggleVar)} />
              }
            </div>
            <div style={{ height: 280, position: 'relative' }}>
              <Map
                ref={(e: any) => { mapRef = e; }}
                onError={(error: any) => { console.log(error) }}
                initialViewState={{ latitude: 38.76246465794007, longitude: -9.11688254220811, zoom: 6 }}
                onRender={(event) => event.target.resize()}
                fadeDuration={0}
                mapStyle={style}
                id="map"
                mapboxAccessToken='pk.eyJ1IjoicHJlZmxldCIsImEiOiJjazdld3VqbGgyZXV5M2RvZ2VvZGFsb2YwIn0.KGf08mMx5U-RW45b7ROq8Q'
              >
                <GeolocateControl
                  position="top-left"
                  onError={(error: any) => { console.log(error) }}
                  trackUserLocation={true}
                  showAccuracyCircle={true}
                  showUserHeading={true}
                  showUserLocation={true}
                  positionOptions={{ enableHighAccuracy: true }}
                />
                <FullscreenControl containerId='full-screen' key={'id-full'} position="top-left" />
                <NavigationControl position="top-left" showCompass={false} />
                {state.map.length && state.map.map((m: any, index: number) => (
                  <div key={index + '-map'}>
                    <Marker
                      key={index + 1}
                      latitude={Number(m.lat)}
                      longitude={Number(m.lon)}
                      color={m.color}
                      style={{ cursor: 'pointer' }}
                      scale={0.5}
                      onClick={() => openAssets(m.name)}
                    >
                      <div style={{ width: '1.55rem', height: '1.55rem', backgroundColor: `${m.color}`, borderRadius: '2rem', padding: '6.5px', boxShadow: 'rgba(0,0,0,0.35) 0px 5px 15px' }}>
                        <div style={{ width: '.75rem', height: '.75rem', backgroundColor: `${m.assetStatus}`, borderRadius: '2rem' }} />
                      </div>
                    </Marker>
                  </div>
                ))}
                <img src={AssetIconBulb} onClick={() => FlyToLocation()} className="fly-to-button-full-screen" alt="icon" />
                {assetLayoutModal && full ? <AssetOnMapModal fullScreen={true} id={assetId} key={'modal-map-' + assetId} /> : ''}
              </Map>
            </div>
          </div>
        </div>

        {/* ── Charts ── */}
        <div style={{ padding: '12px 12px 0' }}>
          {/* Donut Chart */}
          <div style={{
            background: '#fff',
            borderRadius: 20,
            overflow: 'hidden',
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            marginBottom: 12,
          }}>
            <div style={{ padding: '12px 16px 10px', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#1d508d' }}>{t('Home.map.donut')}</p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
              {isEmpty(state.dashboard.maintenance_count.count) ? (
                <div style={{ textAlign: 'center', padding: '24px 0', color: '#bbb' }}>
                  <IonIcon style={{ fontSize: '4rem' }} src={analytics} />
                  <p style={{ margin: '8px 0 0', fontSize: 13 }}>{t('Utils.noGraph')}</p>
                </div>
              ) : (
                <Plot
                  style={{ display: 'flex', justifyContent: 'center' }}
                  data={[{
                    labels: state.dashboard.maintenance_count.labels,
                    values: state.dashboard.maintenance_count.count,
                    type: 'pie',
                    hole: 0.5,
                    marker: { colors: ['#FF9088', '#7AABE5', '#FCB454'] },
                  }]}
                  layout={{ yaxis: { fixedrange: true }, xaxis: { fixedrange: true }, margin: { r: 0, t: 0, l: 0, b: 0 }, font: { family: 'Nunito Sans' }, width: 300, autosize: true }}
                  config={{ displayModeBar: false }}
                />
              )}
            </div>
          </div>

          {/* Bar Chart */}
          <div style={{
            background: '#fff',
            borderRadius: 20,
            overflow: 'hidden',
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            marginBottom: 12,
          }}>
            <div style={{ padding: '12px 16px 10px', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#1d508d' }}>{t('Home.map.bar')}</p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
              {isEmpty(state.dashboard.repair_req_count.x) ? (
                <div style={{ textAlign: 'center', padding: '24px 0', color: '#bbb' }}>
                  <IonIcon style={{ fontSize: '4rem' }} src={analytics} />
                  <p style={{ margin: '8px 0 0', fontSize: 13 }}>{t('Utils.noGraph')}</p>
                </div>
              ) : (
                <Plot
                  data={[{
                    y: state.dashboard.repair_req_count.x,
                    x: state.dashboard.repair_req_count.y,
                    type: 'bar',
                    textposition: 'auto',
                    marker: { color: colors },
                  }]}
                  layout={{ yaxis: { fixedrange: true, automargin: true }, xaxis: { fixedrange: true, automargin: true }, margin: { r: 5, t: 10, l: 5, b: 10 }, font: { family: 'Nunito Sans' }, width: 300, autosize: true }}
                  config={{ displayModeBar: false }}
                />
              )}
            </div>
          </div>

          {/* Tasks Pie */}
          <div style={{
            background: '#fff',
            borderRadius: 20,
            overflow: 'hidden',
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            marginBottom: 24,
          }}>
            <div style={{ padding: '12px 16px 10px', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#1d508d' }}>{t('Home.map.task')}</p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
              {isEmpty(state?.dashboard?.tasks_count?.count) ? (
                <div style={{ textAlign: 'center', padding: '24px 0', color: '#bbb' }}>
                  <IonIcon style={{ fontSize: '4rem' }} src={analytics} />
                  <p style={{ margin: '8px 0 0', fontSize: 13 }}>{t('Utils.noGraph')}</p>
                </div>
              ) : (
                <Plot
                  data={[{
                    labels: state.dashboard.tasks_count.labels,
                    values: state.dashboard.tasks_count.count,
                    type: 'pie',
                    marker: { colors: colors.reverse() },
                  }]}
                  layout={{ yaxis: { fixedrange: true }, xaxis: { fixedrange: true }, legend: { x: 0.1, y: -0.2, xanchor: 'center', orientation: 'h' }, margin: { r: 0, t: 0, l: 0, b: 0 }, font: { family: 'Nunito Sans' }, width: 300, autosize: true }}
                  config={{ displayModeBar: false }}
                />
              )}
            </div>
          </div>
        </div>

        <ScanButton isAssetPage={true} />
        <br />
      </IonContent>
      {assetLayoutModal && !full ? <AssetOnMapModal fullScreen={false} id={assetId} key={'modal-map-' + assetId} /> : ''}
    </IonPage>
  );
};

export default Home;
